import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNotification } from '../../../../contexts/NotificationContext';
import { DRAFT_KEYS, loadDraft, hasDraft, clearDraft, saveDraft } from '../../../../utils/draftUtils';
import { 
  fetchPostOpOrders, 
  savePostOpOrders, 
  updatePostOpOrders,
  generateShareToken
} from '../api/postOpOrdersApi';
import { mapBackendToForm, mapFormToBackend } from '../utils/mapping';
import { usePostOpOrdersValidation } from './usePostOpOrdersValidation';

export const usePostOpOrders = (uhid_no, navigate) => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();
  
  const [success, setSuccess] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ open: false, message: "", severity: "success" });
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [currentSignatureRole, setCurrentSignatureRole] = useState("");
  const signaturePadRef = useRef(null);

  const [formData, setFormData] = useState({
    uhid_no: uhid_no || '',
    lab_pathology: "No",
    lab_cytology: "No",
    lab_microbiology: "No",
    lab_biochemistry: "No",
    doctor_signature: "", 
    order_datetime: "",
    doctor_name: "",
    doctor_name_signature: ""
  });

  const { errors, setErrors, validateField, validateForm } = usePostOpOrdersValidation(formData);

  // Queries
  const { data: existingData } = useQuery({
    queryKey: ['postOpOrders', uhid_no],
    queryFn: () => fetchPostOpOrders(uhid_no),
    enabled: !!uhid_no,
    retry: false
  });

  useEffect(() => {
    if (existingData && Object.keys(existingData).length > 0) {
      setSuccess(true);
      setFormData(prev => ({ ...prev, ...mapBackendToForm(existingData) }));
    }
  }, [existingData]);

  // Draft loading and saving
  useEffect(() => {
    if (uhid_no && hasDraft(DRAFT_KEYS.POST_OPERATIVE_ORDERS, uhid_no)) {
      const saved = loadDraft(DRAFT_KEYS.POST_OPERATIVE_ORDERS, uhid_no);
      if (saved && saved.formData) {
        setFormData(prev => ({ ...prev, ...saved.formData }));
      }
    }
  }, [uhid_no]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (uhid_no) {
        saveDraft(DRAFT_KEYS.POST_OPERATIVE_ORDERS, uhid_no, formData);
      }
    }, 2000);
    return () => clearTimeout(timeoutId);
  }, [formData, uhid_no]);

  // Handlers
  const handleInputChange = useCallback((event) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  }, [errors, setErrors]);

  const handleBlur = useCallback((event) => {
    const { name, value } = event.target;
    validateField(name, value);
  }, [validateField]);

  // Signature logic
  const handleSignatureOpen = (role) => {
    setCurrentSignatureRole(role);
    setSignatureDialogOpen(true);
  };

  const handleSignatureClear = () => {
    signaturePadRef.current.clear();
  };

  const handleSignatureSave = () => {
    const dataURL = signaturePadRef.current.toDataURL();
    setFormData((prevData) => ({
      ...prevData,
      [currentSignatureRole]: dataURL.split(",")[1],
    }));
    if (errors[currentSignatureRole]) {
      setErrors(prev => ({ ...prev, [currentSignatureRole]: '' }));
    }
    setSignatureDialogOpen(false);
  };

  const handleSignatureClose = () => {
    setSignatureDialogOpen(false);
  };

  // Mutation
  const mutation = useMutation({
    mutationFn: (data) => {
      if (success) {
        return updatePostOpOrders(uhid_no, data);
      } else {
        return savePostOpOrders(data);
      }
    },
    onSuccess: (res) => {
      showNotification('success', res.message || (success ? 'Orders updated successfully!' : 'Orders submitted successfully!'));
      queryClient.invalidateQueries(['postOpOrders', uhid_no]);
      clearDraft(DRAFT_KEYS.POST_OPERATIVE_ORDERS, uhid_no);
      setTimeout(() => {
        navigate(`/mainPage/${uhid_no}`, {
          state: { from: 'PostOperativePhase', status: 'success', step: 3 },
        });
      }, 1000);
    },
    onError: (error) => {
      console.error('Submission error:', error);
      showNotification('error', error.response?.data?.error || 'Error in submitting orders!');
    }
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!validateForm()) return;
    mutation.mutate(mapFormToBackend(formData));
  };

  const handleShare = async () => {
    try {
      const res = await generateShareToken(uhid_no);
      const link = `${window.location.origin}/postOperativeOrders/${uhid_no}?token=${res.token}`;
      const message = `Dear Doctor,\n\nPlease fill the post-operative orders:\n\n${link}`;
      await navigator.clipboard.writeText(link);
      setSubmitStatus({ open: true, message: "Secure link copied to clipboard!", severity: "info" });
      const phoneNumber = '+916379605575';
      window.open(`http://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
    } catch (error) {
      console.error("Error generating share token:", error);
      setSubmitStatus({ open: true, message: "Failed to generate shareable link", severity: "error" });
    }
  };

  return {
    formData, setFormData,
    errors, setErrors,
    success,
    loading: existingData === undefined && !!uhid_no,
    submitStatus, setSubmitStatus,
    signatureDialogOpen, setSignatureDialogOpen,
    signaturePadRef,
    handleInputChange, handleBlur,
    handleSignatureOpen, handleSignatureClear, handleSignatureSave, handleSignatureClose,
    handleSubmit, handleShare,
    mutationLoading: mutation.isLoading
  };
};
